<script setup>
import {computed, ref} from "vue";
import {useMutation} from "@vue/apollo-composable";
import {useRouter} from "vue-router";
import {useTitle} from "@/utils/metadata";
import HpoPinLoginMutation from "@/graphql/mutations/hpo/pinLogin.graphql";
import PinCodeInput from "@/components/hpo/PinCodeInput.vue";

useTitle(pgettext('title', 'HPO PIN Login'));

// The PIN is issued and stored as grouped `NNN-NNN-NNN`; the responder only
// types the digits (a numeric soft keyboard has no hyphen key), so the grouping
// is re-applied here before the value goes to the server.
const PIN_GROUPS = [3, 3, 3];
const PIN_LENGTH = PIN_GROUPS.reduce((total, n) => total + n, 0);

const router = useRouter();
const pin = ref('');
const pinInput = ref(null);
const error = ref('');
const submitting = ref(false);

const complete = computed(() => pin.value.length === PIN_LENGTH);

function formatPin(digits) {
    const parts = [];
    let cursor = 0;
    for (const count of PIN_GROUPS) {
        parts.push(digits.slice(cursor, cursor + count));
        cursor += count;
    }
    return parts.join('-');
}

function onPinUpdate(value) {
    pin.value = value;
    // Keep the rejection visible while the cells are being reset, and drop it
    // as soon as the responder starts a fresh attempt.
    if (value) error.value = '';
}

const {mutate: pinLogin} = useMutation(HpoPinLoginMutation);

async function onSubmit() {
    if (submitting.value || !complete.value) return;

    error.value = '';
    submitting.value = true;
    try {
        const res = await pinLogin({pin: formatPin(pin.value)});
        const payload = res?.data?.hpoPinLoginMutation;
        if (payload?.status === 'ok' && payload.hpo?.id) {
            router.push({name: 'hpo:dashboard', params: {id: payload.hpo.id}});
            return;
        }
        reject();
    } catch (e) {
        reject();
    } finally {
        submitting.value = false;
    }
}

function reject() {
    error.value = gettext('Invalid or expired PIN.');
    // Wipe the cells so the next attempt starts clean -- a partial edit of a
    // rejected PIN would otherwise re-trigger the auto-submit on every keystroke.
    pinInput.value?.clear();
}
</script>

<template>
    <div class="accounts-form__container">
        <div class="accounts-form__window">
            <div class="accounts-form__brand-logo">
                <span class="accounts-form__brand-text">HPO</span>
            </div>
            <span class="accounts-form__header">{{ pgettext('title', 'HPO PIN Login') }}</span>

            <v-form @submit.prevent="onSubmit">
                <PinCodeInput ref="pinInput"
                              :model-value="pin"
                              :groups="PIN_GROUPS"
                              :invalid="!!error"
                              :disabled="submitting"
                              @update:model-value="onPinUpdate"
                              @complete="onSubmit"/>

                <p class="text-center text-medium-emphasis mt-2 mb-3">
                    {{ gettext('Enter the PIN you were given.') }}
                </p>

                <v-alert v-if="error" type="error" class="mb-3">{{ error }}</v-alert>

                <div class="text-center">
                    <v-btn type="submit"
                           color="green"
                           :min-width="200"
                           :disabled="!complete"
                           :loading="submitting">
                        <strong>{{ gettext('Log in') }}</strong>
                    </v-btn>
                </div>
            </v-form>
        </div>
    </div>
    <div class="full-overlay"></div>
</template>
